import { sourceNames, sourceSelectorSchema } from "../workflowData/models";
/**
 * Turn raw source_reference objects ({selector, source_type, source_id, ...})
 * into the human chips the note card renders. Pure and network-free: goal
 * titles come from the draft's own context_options, signed notes retain their
 * stable IDs so the UI can link to them, and everything else falls back to a
 * readable label.
 */

import type { DocumentationSignedNoteOption } from "./models";

export type GoalOption = { goal_id: string; title: string };

export type SourceReferenceChip =
    | { kind: "client_record" }
    | { kind: "client_brain"; label: string; excerpt: string; version?: number }
    | { kind: "goal"; title: string }
    | { kind: "signed_note"; noteId: string; projectId?: string; label?: string }
    | { kind: "workflow_data"; label: string; excerpt: string }
    | { kind: "other"; label: string };

export function signedNoteLabel(noteId: string, notes: DocumentationSignedNoteOption[] = [], language?: string): string {
    const note = notes.find(item => item.note_id === noteId);
    if (!note) return noteId.slice(0, 8);

    const title = note.title.trim() || noteId.slice(0, 8);
    if (typeof note.timestamp !== "number" || !Number.isFinite(note.timestamp)) return title;

    const date = new Date(note.timestamp);
    if (Number.isNaN(date.getTime())) return title;
    return `${date.toLocaleDateString(language)} – ${title}`;
}

export function signedNoteHref(noteId: string): string {
    return `#source-note-${encodeURIComponent(noteId)}`;
}

export function describeSourceReferences(references: Record<string, unknown>[], goals?: GoalOption[]): SourceReferenceChip[] {
    let clientRecord = false;
    // A goal reference whose title cannot be resolved degrades to the generic
    // client-record chip instead of vanishing.
    let unresolvedGoal = false;
    const clientBrainEntries: Array<{
        label: string;
        excerpt: string;
        path: string;
        version?: number;
    }> = [];
    const signedNotes: Array<{ noteId: string; projectId?: string; label?: string }> = [];
    const dataRecords: Array<{ key: string; label: string; excerpt: string }> = [];
    const goalTitles: string[] = [];
    const others: string[] = [];

    for (const reference of references) {
        const selector = typeof reference.selector === "string" ? reference.selector : "";
        const sourceType = typeof reference.source_type === "string" ? reference.source_type : "";
        const sourceId = typeof reference.source_id === "string" ? reference.source_id : "";

        if (sourceType === "signed_note" || selector.startsWith("notes.signed")) {
            const projectId = typeof reference.project_id === "string" ? reference.project_id : undefined;
            const label = typeof reference.source_label === "string" && reference.source_label.trim() ? reference.source_label : undefined;
            if (sourceId && !signedNotes.some(note => note.noteId === sourceId && note.projectId === projectId)) signedNotes.push({ noteId: sourceId, ...(projectId ? { projectId } : {}), ...(label ? { label } : {}) });
        } else if (sourceType === "workflow_data") {
            const parsedSelector = sourceSelectorSchema.safeParse(selector);
            const sourceName = parsedSelector.success ? sourceNames[parsedSelector.data] : "Source record";
            const sourceLabel = typeof reference.source_label === "string" ? reference.source_label : "";
            const excerpt = typeof reference.source_excerpt === "string" ? reference.source_excerpt : "";
            const key = JSON.stringify([selector, reference.project_id, sourceId, reference.source_version]);
            if (excerpt && !dataRecords.some(record => record.key === key)) dataRecords.push({ key, label: sourceLabel && sourceLabel !== sourceId ? `${sourceName}: ${sourceLabel}` : sourceName, excerpt });
        } else if (sourceType === "goal" || selector === "goals.active") {
            const title = goals?.find(goal => goal.goal_id === sourceId)?.title || "";
            if (title && !goalTitles.includes(title)) goalTitles.push(title);
            else if (!title) unresolvedGoal = true;
        } else if (sourceType === "client_brain" || selector.startsWith("client.")) {
            const label = typeof reference.source_label === "string" ? reference.source_label.trim() : "";
            const excerpt = typeof reference.source_excerpt === "string" ? reference.source_excerpt.trim() : "";
            const path = typeof reference.source_path === "string" ? reference.source_path.trim() : "";
            const version =
                typeof reference.source_version === "number" && Number.isFinite(reference.source_version)
                    ? reference.source_version
                    : undefined;
            if (label && excerpt) {
                const duplicate = clientBrainEntries.some(entry =>
                    path
                        ? entry.path === path && entry.version === version
                        : entry.label === label && entry.excerpt === excerpt && entry.version === version
                );
                if (!duplicate) clientBrainEntries.push({ label, excerpt, path, version });
            } else {
                clientRecord = true;
            }
        } else {
            const label = selector || sourceType;
            if (label && !others.includes(label)) others.push(label);
        }
    }

    const chips: SourceReferenceChip[] = [];
    if (clientRecord || unresolvedGoal) chips.push({ kind: "client_record" });
    for (const entry of clientBrainEntries) {
        chips.push({
            kind: "client_brain",
            label: entry.label,
            excerpt: entry.excerpt,
            version: entry.version
        });
    }
    for (const title of goalTitles) chips.push({ kind: "goal", title });
    for (const note of signedNotes) chips.push({ kind: "signed_note", ...note });
    for (const record of dataRecords) chips.push({ kind: "workflow_data", label: record.label, excerpt: record.excerpt });
    for (const label of others) chips.push({ kind: "other", label });
    return chips;
}

export function describeVisibleSourceReferences(
    references: Record<string, unknown>[],
    sourceLinksOnly: boolean,
    goals?: GoalOption[]
): SourceReferenceChip[] {
    return describeSourceReferences(references, goals).filter(chip =>
        sourceLinksOnly ? chip.kind === "signed_note" : chip.kind !== "signed_note"
    );
}
